import { isSuperUser, getDeptCodeByEmail } from "../config/roles";
import { getDeptNameByCode } from "../constants/departments";
import type { AppUser } from "../context/AuthContext";

/**
 * Normalizes strings for robust matching (trim, ignore case, ignore "部" suffix)
 */
function normalize(str: any): string {
    if (!str) return "";
    return String(str)
        .trim()
        .toLowerCase()
        .replace(/\s/g, "")
        .replace(/部$/, ""); // "行銷設計部" -> "行銷設計"
}

/**
 * Universal Data Visibility Check
 * 
 * Rules:
 * 1. Super Users can view EVERYTHING.
 * 2. Regular Department Accounts can only view data if:
 *    - They are the "owner" (department name or code match)
 *    - They are the original requester (id match)
 *    - The document is explicitly shared with them or they are assigned
 */
export function canViewData(user: AppUser | null, doc: any): boolean {
  if (!user || !doc) return false;

  // ✅ Seed cards are global baseline obligations/training data.
  // They should be visible regardless of department scoping.
  if ((doc as any)?.isSeed === true) return true;

  // 1. Super User Override
  if (isSuperUser(user.email)) return true;

  // 2. Identity normalization
  const userDeptCode = getDeptCodeByEmail(user.email).toUpperCase();
  const userDeptName = getDeptNameByCode(userDeptCode);
  
  const nUserDept = normalize(userDeptName);
  const nUserCode = normalize(userDeptCode);

  // 3. Document fields normalization
  const docDepts = [
    doc.department,
    doc.applicantDept,
    doc.ourDept,
    doc.ownerDept,
    doc.responsibleDept,
    doc.mainDepartment,
    doc.createdDept,
    doc.dept
  ].map(normalize);

  const docCodes = [
    doc.deptCode,
    doc.ownerDeptCode,
    doc.applicantDeptCode,
    doc.primaryDeptCode,
    doc.createdByDeptId,
    doc.assigneeDepartmentCode
  ].map(normalize);

  // 4. Match Logic
  // A. Dept Name Match
  if (docDepts.some(d => d === nUserDept)) return true;
  
  // B. Dept Code Match
  if (docCodes.some(c => c === nUserCode)) return true;

  // C. Creator / Requester ID Match
  if (doc.requesterUid === user.id || doc.createdBy === user.id || doc.userId === user.id) return true;

  // D. Shared Access (Arrays)
  const shared = [
    ...(Array.isArray(doc.visibleDepartments) ? doc.visibleDepartments : []),
    ...(Array.isArray(doc.visibleDepts) ? doc.visibleDepts : []),
    ...(Array.isArray(doc.coDepts) ? doc.coDepts : []),
    ...(Array.isArray(doc.sharedWithDepts) ? doc.sharedWithDepts : []),
    ...(Array.isArray(doc.assignedDeptCodes) ? doc.assignedDeptCodes : [])
  ].map(normalize);

  if (shared.some(s => s === nUserDept || s === nUserCode)) return true;

  return false;
}